import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, HStack, FormControl, FormLabel, Input, Select, Textarea, Button, Progress, SimpleGrid, Icon } from "@chakra-ui/react";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { FaUser, FaHome, FaDollarSign, FaCheckCircle } from "react-icons/fa";

const FinanceApply: React.FC = () => {
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  return (
    <Box minH="100vh" bg="white">
      <SEOHead pagePath="/finance/apply" fallbackTitle="Finance Application" fallbackDescription="Apply for a loan or finance with One Group" />
      <NavbarWithCallToAction />
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">Finance Section</Text>
            <Heading size="2xl" fontWeight="bold">Apply for Finance</Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">Submit your finance application</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.lg" py={20} flex="1">
        <VStack spacing={12}>
          {/* Progress Bar */}
          <Box w="full">
            <HStack justify="space-between" mb={4}>
              <HStack>
                <Icon as={FaUser} color={step >= 1 ? "blue.500" : "gray.300"} />
                <Text fontSize="sm" fontWeight={step === 1 ? 600 : 400}>Personal</Text>
              </HStack>
              <HStack>
                <Icon as={FaHome} color={step >= 2 ? "blue.500" : "gray.300"} />
                <Text fontSize="sm" fontWeight={step === 2 ? 600 : 400}>Loan Details</Text>
              </HStack>
              <HStack>
                <Icon as={FaDollarSign} color={step >= 3 ? "blue.500" : "gray.300"} />
                <Text fontSize="sm" fontWeight={step === 3 ? 600 : 400}>Financial Info</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color={step >= 4 ? "blue.500" : "gray.300"} />
                <Text fontSize="sm" fontWeight={step === 4 ? 600 : 400}>Review</Text>
              </HStack>
            </HStack>
            <Progress value={progress} colorScheme="blue" borderRadius="full" />
          </Box>

          {/* Form Steps */}
          <Box bg="white" p={8} borderRadius="xl" border="1px solid" borderColor="gray.200" shadow="lg" w="full">
            {step === 1 && (
              <VStack spacing={6} align="stretch">
                <Heading size="lg" mb={2}>Personal Information</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel>First Name</FormLabel>
                    <Input placeholder="John" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Last Name</FormLabel>
                    <Input placeholder="Smith" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input type="email" placeholder="john.smith@email.com" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Phone</FormLabel>
                    <Input placeholder="0412 345 678" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Date of Birth</FormLabel>
                    <Input type="date" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Marital Status</FormLabel>
                    <Select size="lg">
                      <option>Single</option>
                      <option>Married</option>
                      <option>De Facto</option>
                      <option>Divorced</option>
                    </Select>
                  </FormControl>
                </SimpleGrid>
                <FormControl isRequired>
                  <FormLabel>Residential Address</FormLabel>
                  <Input placeholder="123 Main Street, Suburb, State, 3000" size="lg" />
                </FormControl>
              </VStack>
            )}

            {step === 2 && (
              <VStack spacing={6} align="stretch">
                <Heading size="lg" mb={2}>Loan Details</Heading>
                <FormControl isRequired>
                  <FormLabel>Loan Type</FormLabel>
                  <Select size="lg">
                    <option>Home Loan - Owner Occupied</option>
                    <option>Home Loan - Investment</option>
                    <option>Refinance</option>
                    <option>SMSF Loan</option>
                    <option>Personal Loan</option>
                    <option>Asset Finance</option>
                  </Select>
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel>Loan Amount</FormLabel>
                    <Input type="number" placeholder="500000" size="lg" />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Loan Term (years)</FormLabel>
                    <Input type="number" placeholder="30" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Property Value (if applicable)</FormLabel>
                    <Input type="number" placeholder="650000" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Deposit Amount</FormLabel>
                    <Input type="number" placeholder="130000" size="lg" />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel>Property Address (if known)</FormLabel>
                  <Input placeholder="456 Property Street, Suburb, State, 3000" size="lg" />
                </FormControl>
              </VStack>
            )}

            {step === 3 && (
              <VStack spacing={6} align="stretch">
                <Heading size="lg" mb={2}>Financial Information</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <FormControl isRequired>
                    <FormLabel>Employment Status</FormLabel>
                    <Select size="lg">
                      <option>Full-Time Employee</option>
                      <option>Part-Time Employee</option>
                      <option>Self-Employed</option>
                      <option>Casual</option>
                      <option>Contract</option>
                    </Select>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Annual Income (before tax)</FormLabel>
                    <Input type="number" placeholder="85000" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Other Income (annual)</FormLabel>
                    <Input type="number" placeholder="0" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Monthly Living Expenses</FormLabel>
                    <Input type="number" placeholder="2500" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Credit Card Limit</FormLabel>
                    <Input type="number" placeholder="10000" size="lg" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Other Debts (monthly repayment)</FormLabel>
                    <Input type="number" placeholder="500" size="lg" />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}

            {step === 4 && (
              <VStack spacing={6} align="stretch">
                <Heading size="lg" mb={2}>Review & Submit</Heading>
                <Box bg="blue.50" p={6} borderRadius="lg">
                  <Heading size="md" mb={4}>Application Summary</Heading>
                  <VStack align="start" spacing={2}>
                    <Text><strong>Applicant:</strong> John Smith</Text>
                    <Text><strong>Loan Type:</strong> Home Loan - Owner Occupied</Text>
                    <Text><strong>Loan Amount:</strong> $500,000</Text>
                    <Text><strong>Loan Term:</strong> 30 years</Text>
                    <Text><strong>Annual Income:</strong> $85,000</Text>
                  </VStack>
                </Box>
                <FormControl>
                  <FormLabel>Additional Comments</FormLabel>
                  <Textarea placeholder="Any additional information you'd like to share..." rows={5} />
                </FormControl>
                <Box bg="yellow.50" p={4} borderRadius="lg" fontSize="sm">
                  <Text mb={2}><strong>Declaration:</strong></Text>
                  <Text>I declare that the information provided in this application is true and correct. I understand that providing false information may result in my application being declined or my loan being cancelled.</Text>
                </Box>
              </VStack>
            )}

            {/* Navigation Buttons */}
            <HStack justify="space-between" pt={6}>
              <Button
                onClick={() => setStep(step - 1)}
                isDisabled={step === 1}
                variant="outline"
                colorScheme="gray"
              >
                Previous
              </Button>
              {step < totalSteps ? (
                <Button onClick={() => setStep(step + 1)} colorScheme="blue" size="lg">
                  Next Step
                </Button>
              ) : (
                <Button colorScheme="green" size="lg">
                  Submit Application
                </Button>
              )}
            </HStack>
          </Box>

          {/* Help Text */}
          <Box bg="gray.50" p={6} borderRadius="lg" textAlign="center" w="full">
            <Text fontSize="sm" color="gray.600">
              Need help? Call us on 1300 123 456 or email finance@onegroup.com.au
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceApply;
